import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon, MenuBook as MenuBookIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP } from 'components/financepro';
import { escHtml } from 'facturapos/documentoEmpresarial';
import {
  TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
  fmtMoney,
  formatearFecha,
  cardShellSx,
  cellHeadSx,
} from './entradaDiarioShared';

const moneyHtml = (n) => `$${fmtMoney(n)}`;

const generarHtmlAsiento = ({
  nombreEmpresa,
  direccionEmpresa,
  rncEmpresa,
  tituloDocumento,
  numero,
  documento,
  fecha,
  concepto,
  tasa,
  creado,
  lineas,
  totalDebito,
  totalCredito,
}) => {
  const numeroMostrar = documento && numero != null ? `${documento}-${numero}` : String(numero || '—');
  const filas =
    !lineas || lineas.length === 0
      ? `<tr><td colspan="6" style="text-align:center;padding:18px;color:#888;">Sin líneas</td></tr>`
      : lineas
          .map(
            (ln, idx) => `
        <tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f7f8fa'};">
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(ln.cuenta || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(ln.nombre || ln.nombre_cuenta || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(ln.concepto || '—')}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;font-size:12px;">${escHtml(
            ln.centro_nombre || (ln.centrodecosto ? String(ln.centrodecosto) : '—')
          )}</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${
            Number(ln.debito || 0) > 0 ? moneyHtml(ln.debito) : ''
          }</td>
          <td style="padding:8px;border-bottom:1px solid #e6e8ec;text-align:right;font-size:12px;">${
            Number(ln.credito || 0) > 0 ? moneyHtml(ln.credito) : ''
          }</td>
        </tr>`
          )
          .join('');

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escHtml(tituloDocumento)} ${escHtml(numeroMostrar)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
  .bar { height: 4px; background: #0E1F33; margin: -24px -24px 18px; }
  .empresa { font-size: 18px; font-weight: 700; }
  .muted { color: #5A6B85; font-size: 12px; margin: 2px 0; }
  h1 { font-size: 16px; letter-spacing: 0.08em; text-transform: uppercase; margin: 18px 0 8px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #0E1F33; color: #fff; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; padding: 8px; text-align: left; }
  th.r, td.r { text-align: right; }
  .tot { font-weight: 700; background: #EEF2F8; }
  .meta { margin: 12px 0 18px; }
  @media print { body { margin: 12px; } }
</style></head><body>
  <div class="bar"></div>
  <div class="empresa">${escHtml(nombreEmpresa || 'Empresa')}</div>
  ${direccionEmpresa ? `<div class="muted">${escHtml(direccionEmpresa)}</div>` : ''}
  ${rncEmpresa ? `<div class="muted">RNC: ${escHtml(rncEmpresa)}</div>` : ''}
  <h1>${escHtml(tituloDocumento)}</h1>
  <div class="meta">
    <div><strong>Documento:</strong> ${escHtml(numeroMostrar)}</div>
    <div><strong>Fecha:</strong> ${escHtml(formatearFecha(fecha))}</div>
    ${tasa ? `<div><strong>Tasa:</strong> ${escHtml(fmtMoney(tasa))}</div>` : ''}
    ${creado ? `<div><strong>Registrado por:</strong> ${escHtml(creado)}</div>` : ''}
    <div><strong>Concepto:</strong> ${escHtml(concepto || '—')}</div>
  </div>
  <table>
    <thead><tr>
      <th>Cuenta</th><th>Nombre</th><th>Concepto</th><th>Centro</th>
      <th class="r">Débito</th><th class="r">Crédito</th>
    </tr></thead>
    <tbody>${filas}
      <tr class="tot">
        <td colspan="4" style="padding:10px 8px;">Totales</td>
        <td class="r" style="padding:10px 8px;">${moneyHtml(totalDebito)}</td>
        <td class="r" style="padding:10px 8px;">${moneyHtml(totalCredito)}</td>
      </tr>
    </tbody>
  </table>
</body></html>`;
};

/**
 * Vista y reimpresión de un asiento de diario (Ajuste Entradas).
 * Reutilizable desde el resumen o inmediatamente después de registrar.
 */
const VistaEntradaDiario = ({ open, onClose, entrada = {}, autoPrint = false }) => {
  const { controller, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const printedRef = useRef(false);

  const nombreEmpresa =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : 'Empresa';
  const direccionEmpresa =
    typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim() ? direccionEmpresaCtx.trim() : '';
  const rncEmpresa = typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';

  const documento = entrada.documento || TIPO_DOCUMENTO_AJUSTE_ENTRADAS;
  const numero = entrada.numero;
  const fecha = entrada.fecha || '';
  const concepto = entrada.concepto || '';
  const tasa = entrada.tasa;
  const creado = entrada.creado || '';
  const lineas = entrada.lineas || [];
  const totalDebito = Number(entrada.total_debito ?? lineas.reduce((s, l) => s + (parseFloat(l.debito) || 0), 0));
  const totalCredito = Number(entrada.total_credito ?? lineas.reduce((s, l) => s + (parseFloat(l.credito) || 0), 0));
  const cuadrada = Math.abs(totalDebito - totalCredito) < 0.01;

  const htmlAsiento = useMemo(
    () =>
      generarHtmlAsiento({
        nombreEmpresa,
        direccionEmpresa,
        rncEmpresa,
        tituloDocumento: documento,
        numero,
        documento,
        fecha,
        concepto,
        tasa,
        creado,
        lineas,
        totalDebito,
        totalCredito,
      }),
    [
      nombreEmpresa,
      direccionEmpresa,
      rncEmpresa,
      documento,
      numero,
      fecha,
      concepto,
      tasa,
      creado,
      lineas,
      totalDebito,
      totalCredito,
    ]
  );

  const handleImprimir = useCallback(() => {
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    ventana.document.open();
    ventana.document.write(htmlAsiento);
    ventana.document.close();
    setTimeout(() => {
      ventana.focus();
      ventana.print();
    }, 250);
  }, [htmlAsiento, showError]);

  useEffect(() => {
    if (open && autoPrint && !printedRef.current && (lineas.length > 0 || numero != null)) {
      printedRef.current = true;
      const t = setTimeout(handleImprimir, 400);
      return () => clearTimeout(t);
    }
    if (!open) printedRef.current = false;
    return undefined;
  }, [open, autoPrint, lineas.length, numero, handleImprimir]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          overflow: 'hidden',
          width: { xs: '96vw', md: '90vw' },
          maxWidth: 1280,
          height: { xs: '88vh', md: '86vh' },
          maxHeight: { xs: '88vh', md: '86vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.2, md: 2.6 },
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MenuBookIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem', flex: 1 }}>
          {documento} {numero != null ? `Nº ${numero}` : ''}
        </Typography>
        <Chip
          size="small"
          label={cuadrada ? 'Cuadrada' : 'Descuadrada'}
          sx={{
            fontWeight: 700,
            bgcolor: cuadrada ? FP.color.successSoft : FP.color.dangerSoft,
            color: cuadrada ? FP.color.successDark : FP.color.danger,
          }}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2.5, md: 4 },
          pb: { xs: 2.5, md: 3 },
          bgcolor: FP.color.surface,
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 3, md: 4 },
          },
        }}
      >
        <Paper elevation={0} sx={{ ...cardShellSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: FP.color.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Fecha
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatearFecha(fecha)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: FP.color.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Tasa
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{fmtMoney(tasa)}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: FP.color.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Registrado por
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{creado || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: FP.color.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Concepto
              </Typography>
              <Typography>{concepto || '—'}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Cuentas del asiento
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 860, sm: 980 } }}>
                <colgroup>
                  {['14%', '22%', '24%', '16%', '12%', '12%'].map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Cuenta
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Nombre
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Concepto
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Centro
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Débito
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Crédito
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {lineas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: FP.color.textMuted }}>
                        Sin líneas
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineas.map((ln, i) => (
                      <TableRow key={`${ln.cuenta}-${i}`} hover>
                        <TableCell
                          sx={{ fontWeight: 700, color: FP.color.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.cuenta || '—'}
                        >
                          {ln.cuenta}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.nombre || ln.nombre_cuenta || '—'}
                        >
                          {ln.nombre || ln.nombre_cuenta || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.concepto || '—'}
                        >
                          {ln.concepto || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.centro_nombre || ln.centrodecosto || '—'}
                        >
                          {ln.centro_nombre || ln.centrodecosto || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {Number(ln.debito || 0) > 0 ? `$${fmtMoney(ln.debito)}` : ''}
                        </TableCell>
                        <TableCell align="right">
                          {Number(ln.credito || 0) > 0 ? `$${fmtMoney(ln.credito)}` : ''}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  <TableRow
                    sx={{
                      '& td': {
                        borderTop: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                        fontWeight: 800,
                      },
                    }}
                  >
                    <TableCell colSpan={4}>Totales</TableCell>
                    <TableCell align="right">${fmtMoney(totalDebito)}</TableCell>
                    <TableCell align="right">${fmtMoney(totalCredito)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          startIcon={<PrintIcon />}
          onClick={handleImprimir}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.4,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
            '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
          }}
        >
          Imprimir
        </Button>
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            bgcolor: FP.color.navy,
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VistaEntradaDiario;
